/**
 * Display formatting for service / add-on / category names.
 *
 * Admins sometimes enter names in all lowercase ("mani pedi") or with
 * inconsistent capitalization ("Acrylic big toe"). This normalizes them to
 * proper title case for display, while preserving acronyms (XL, XXL, CEO),
 * hyphenated words (Fill-In, Soak-Off) and emoji.
 */

// Words kept lowercase unless they are the first or last word.
const MINOR_WORDS = new Set([
  "a", "an", "and", "as", "at", "but", "by", "for", "from", "in", "into",
  "nor", "of", "on", "onto", "or", "per", "the", "to", "up", "via", "with",
]);

// Tokens with a fixed casing that should never be changed.
const FIXED: Record<string, string> = {
  xl: "XL",
  xxl: "XXL",
  xs: "XS",
  ceo: "CEO",
  bday: "BDAY",
  diy: "DIY",
  uv: "UV",
  led: "LED",
  ii: "II",
  iii: "III",
};

const hasLetters = (s: string) => /[a-z]/i.test(s);

const capitalizePart = (part: string): string => {
  if (!part) return part;
  const key = part.toLowerCase();
  if (FIXED[key]) return FIXED[key];
  // Leading non-letters (emoji, punctuation, digits) stay untouched.
  const idx = part.search(/[a-z]/i);
  if (idx === -1) return part;
  return part.slice(0, idx) + part[idx].toUpperCase() + part.slice(idx + 1).toLowerCase();
};

const formatWord = (word: string): string =>
  // Keep separators (- / .) and capitalize each segment: "fill-in" -> "Fill-In"
  word
    .split(/([-/.])/)
    .map((seg) => (seg === "-" || seg === "/" || seg === "." ? seg : capitalizePart(seg)))
    .join("");

export const toTitleCase = (input?: string | null): string => {
  if (!input) return "";
  const words = input.trim().split(/(\s+)/); // keep whitespace
  const meaningful = words.filter((w) => w.trim() && hasLetters(w));
  const first = meaningful[0];
  const last = meaningful[meaningful.length - 1];

  return words
    .map((word) => {
      if (!word.trim()) return word;
      if (!hasLetters(word)) return word; // "+", "&", numbers, emoji

      // Preserve deliberate all-caps acronyms typed by the admin (2-4 letters).
      const letters = word.replace(/[^a-zA-Z]/g, "");
      if (letters.length > 1 && letters.length <= 4 && letters === letters.toUpperCase()) {
        return word;
      }

      const bare = word.replace(/[^a-zA-Z]/g, "").toLowerCase();
      if (MINOR_WORDS.has(bare) && word !== first && word !== last) {
        return word.toLowerCase();
      }
      return formatWord(word);
    })
    .join("");
};
